import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Form, Spinner, Alert, Badge, Pagination, Nav, Row, Col } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import apiClient from '../api/client';
import DieselMap from './DieselMap';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ── Constantes de categorías ─────────────────────────────────────────────────
const CATEGORIES = ['Todos', 'Diésel UBA', 'Diésel', 'Gasolina Premium', 'Gasolina Regular'];

const CATEGORY_STYLE = {
    'Todos':            { variant: 'secondary', icon: 'fa-list' },
    'Diésel UBA':       { variant: 'info',      icon: 'fa-leaf' },
    'Diésel':           { variant: 'dark',       icon: 'fa-truck' },
    'Gasolina Premium': { variant: 'warning',    icon: 'fa-star' },
    'Gasolina Regular': { variant: 'success',    icon: 'fa-gas-pump' },
};

const BITACORA_MUNICIPIOS = [
    { entidad: 'Ciudad de México',      municipio: 'Miguel Hidalgo' },
    { entidad: 'Coahuila de Zaragoza',  municipio: 'Ramos Arizpe' },
    { entidad: 'Nuevo León',            municipio: 'Apodaca' },
    { entidad: 'Nuevo León',            municipio: 'Cadereyta Jiménez' },
    { entidad: 'Nuevo León',            municipio: 'General Escobedo' },
    { entidad: 'Querétaro',             municipio: 'Querétaro' },
    { entidad: 'San Luis Potosí',       municipio: 'Matehuala' },
    { entidad: 'Tamaulipas',            municipio: 'Nuevo Laredo' },
    { entidad: 'Tamaulipas',            municipio: 'Reynosa' },
];

// Normaliza un campo texto: quita espacios extremos y normaliza Unicode
const normalizeText = s => (s || '').trim().normalize('NFC');

// Clasifica un registro en una de las 4 categorías de combustible
const categorizeItem = item => {
    const desc = normalizeText(item.SubProducto).toLowerCase();
    const prod = normalizeText(item.Producto).toLowerCase();
    if (desc.includes('ultra bajo azufre') || desc.includes('duba'))   return 'Diésel UBA';
    if (desc.includes('premium'))                                        return 'Gasolina Premium';
    if (desc.includes('regular'))                                        return 'Gasolina Regular';
    if (prod.includes('diesel')  || prod.includes('diésel')  ||
        desc.includes('diesel')  || desc.includes('diésel')  ||
        desc.includes('automotriz') || desc.includes('agrícola') ||
        desc.includes('agricola')   || desc.includes('marino'))          return 'Diésel';
    return 'Otro';
};

// Limpia y enriquece cada registro con campos normalizados + categoría
const normalizeItem = item => ({
    ...item,
    Producto:    normalizeText(item.Producto),
    SubProducto: normalizeText(item.SubProducto),
    categoria:   categorizeItem(item),
});

const DieselPrices = () => {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('datos');
    const [stateSortConfig, setStateSortConfig] = useState({ key: 'avg', direction: 'ascending' });
    const [filters, setFilters] = useState({
        entidad: '',
        municipio: '',
        razonSocial: '',
        producto: '',
        descripcion: '',
        direccion: ''
    });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [selectedCategories, setSelectedCategories] = useState(['Diésel']);

    const toggleCategory = (cat) => {
        if (cat === 'Todos') {
            setSelectedCategories(['Todos']);
        } else {
            setSelectedCategories(prev => {
                const without = prev.filter(c => c !== 'Todos');
                const next = without.includes(cat) ? without.filter(c => c !== cat) : [...without, cat];
                return next.length === 0 ? ['Todos'] : next;
            });
        }
        setCurrentPage(1);
    };
    const [showBitacora, setShowBitacora] = useState(false);
    const [expandedStates, setExpandedStates] = useState(new Set());
    const [stationSortConfig, setStationSortConfig] = useState({});

    const handleStationSort = (stateName, key) => {
        setStationSortConfig(prev => {
            const current = prev[stateName] || { key: null, direction: 'ascending' };
            const direction = current.key === key && current.direction === 'ascending' ? 'descending' : 'ascending';
            return { ...prev, [stateName]: { key, direction } };
        });
    };

    const renderStationSortIcon = (stateName, key) => {
        const cfg = stationSortConfig[stateName];
        if (!cfg || cfg.key !== key) return <i className="fas fa-sort text-muted ml-1" style={{ opacity: 0.3 }}></i>;
        return cfg.direction === 'ascending'
            ? <i className="fas fa-sort-up ml-1"></i>
            : <i className="fas fa-sort-down ml-1"></i>;
    };

    const toggleStateExpand = (name) => {
        setExpandedStates(prev => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);

    const fetchAllPrices = async () => {
        setLoading(true);
        setError(null);
        setResults([]);
        setCurrentPage(1); // Reset to first page on new fetch

        try {
            // Llamada al endpoint backend que maneja la concurrencia
            const { data } = await apiClient.get(`/api/diesel/fetch-all`);

            if (data.status === 'success' && Array.isArray(data.data)) {
                setResults(data.data.map(normalizeItem));
            } else {
                throw new Error('Formato de respuesta inesperado');
            }

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Reset pagination when filter or category changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, selectedCategories]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            entidad: '',
            municipio: '',
            razonSocial: '',
            producto: '',
            descripcion: '',
            direccion: ''
        });
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Filtrado y ordenamiento de resultados
    const filteredResults = useMemo(() => {
        let items = results.filter(item => {
            if (showBitacora) {
                const inBitacora = BITACORA_MUNICIPIOS.some(b =>
                    (item.entidadNombre || '').toLowerCase().includes(b.entidad.toLowerCase()) &&
                    (item.municipioNombre || '').toLowerCase().includes(b.municipio.toLowerCase())
                );
                if (!inBitacora) return false;
            }

            if (!selectedCategories.includes('Todos') && !selectedCategories.includes(item.categoria)) return false;
            const entMatch = !filters.entidad || 
                             (item.entidadNombre && item.entidadNombre.toLowerCase().includes(filters.entidad.toLowerCase())) ||
                             (item.entidadId && item.entidadId.toString().includes(filters.entidad));
            
            const munMatch = !filters.municipio || 
                             (item.municipioNombre && item.municipioNombre.toLowerCase().includes(filters.municipio.toLowerCase())) ||
                             (item.municipioId && item.municipioId.toString().includes(filters.municipio));
            
            const prodMatch = !filters.producto || 
                              (item.Producto && item.Producto.toLowerCase().includes(filters.producto.toLowerCase()));
            
            const descMatch = !filters.descripcion || 
                              (item.SubProducto && item.SubProducto.toLowerCase().includes(filters.descripcion.toLowerCase()));

            const dirMatch = !filters.direccion || 
                              (item.Direccion && item.Direccion.toLowerCase().includes(filters.direccion.toLowerCase()));

            const razonMatch = !filters.razonSocial ||
                               (item.Nombre && item.Nombre.toLowerCase().includes(filters.razonSocial.toLowerCase()));
                              
            return entMatch && munMatch && prodMatch && descMatch && dirMatch && razonMatch;
        });

        if (sortConfig.key !== null) {
            items.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Manejo especial para columnas compuestas (Entidad/Municipio)
                if (sortConfig.key === 'entidadId') {
                    aValue = a.entidadNombre || a.entidadId;
                    bValue = b.entidadNombre || b.entidadId;
                } else if (sortConfig.key === 'municipioId') {
                    aValue = a.municipioNombre || a.municipioId;
                    bValue = b.municipioNombre || b.municipioId;
                }

                // Manejo de nulos
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';

                // Comparación numérica para Precio
                if (sortConfig.key === 'PrecioVigente' || sortConfig.key === 'Numero') {
                    return sortConfig.direction === 'ascending' 
                        ? parseFloat(aValue) - parseFloat(bValue)
                        : parseFloat(bValue) - parseFloat(aValue);
                }

                // Comparación de texto
                aValue = aValue.toString().toLowerCase();
                bValue = bValue.toString().toLowerCase();

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return items;
    }, [results, filters, sortConfig, showBitacora, selectedCategories]);

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredResults.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredResults.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // ── Métricas ────────────────────────────────────────────────────────────────
    const metrics = useMemo(() => {
        if (filteredResults.length === 0) return null;

        const base = selectedCategories.includes('Todos')
            ? filteredResults
            : filteredResults.filter(r => selectedCategories.includes(r.categoria));

        const prices = base.map(r => parseFloat(r.PrecioVigente)).filter(p => !isNaN(p) && p > 0);
        if (prices.length === 0) return null;

        const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const minItem = base.find(r => parseFloat(r.PrecioVigente) === min);
        const maxItem = base.find(r => parseFloat(r.PrecioVigente) === max);

        // Agrupar por estado
        const byState = {};
        base.forEach(r => {
            const key = r.entidadNombre || `Entidad ${r.entidadId}`;
            const p = parseFloat(r.PrecioVigente);
            if (!key || isNaN(p) || p <= 0) return;
            if (!byState[key]) byState[key] = [];
            byState[key].push(p);
        });

        const stateStats = Object.entries(byState).map(([name, ps]) => ({
            name,
            count: ps.length,
            min: Math.min(...ps),
            max: Math.max(...ps),
            avg: ps.reduce((a, b) => a + b, 0) / ps.length,
            spread: Math.max(...ps) - Math.min(...ps),
        })).sort((a, b) => a.avg - b.avg);

        // Histograma de distribución de precios
        const buckets = {};
        prices.forEach(p => {
            const floor = Math.floor(p);
            const key = `$${floor}-${floor + 1}`;
            buckets[key] = (buckets[key] || 0) + 1;
        });

        return { total: results.length, baseCount: base.length, avg, min, max, spread: max - min, minItem, maxItem, stateStats, buckets };
    }, [filteredResults, selectedCategories, results.length]);

    // Conteo por categoría sobre los resultados filtrados
    const categoryCounts = useMemo(() => {
        const counts = { 'Todos': filteredResults.length };
        CATEGORIES.filter(c => c !== 'Todos').forEach(c => { counts[c] = 0; });
        filteredResults.forEach(r => { if (counts[r.categoria] !== undefined) counts[r.categoria]++; });
        return counts;
    }, [filteredResults]);

    // Ordenamiento de tabla de estados
    const sortedStateStats = useMemo(() => {
        if (!metrics) return [];
        const items = [...metrics.stateStats];
        items.sort((a, b) => {
            const av = a[stateSortConfig.key];
            const bv = b[stateSortConfig.key];
            if (typeof av === 'string') return stateSortConfig.direction === 'ascending' ? av.localeCompare(bv) : bv.localeCompare(av);
            return stateSortConfig.direction === 'ascending' ? av - bv : bv - av;
        });
        return items;
    }, [metrics, stateSortConfig]);

    const handleStateSort = (key) => {
        setStateSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
        }));
    };

    // Datos para gráfica de precios por estado (horizontal bar)
    const stateChartData = useMemo(() => {
        if (!metrics) return null;
        const stats = metrics.stateStats; // ya ordenado por avg asc
        const minAvg = stats[0]?.avg ?? 0;
        const maxAvg = stats[stats.length - 1]?.avg ?? 0;
        const range = maxAvg - minAvg || 1;
        const colors = stats.map(s => {
            const t = (s.avg - minAvg) / range;
            const r = Math.round(t * 200 + 30);
            const g = Math.round((1 - t) * 170 + 30);
            return `rgba(${r}, ${g}, 60, 0.8)`;
        });
        return {
            labels: stats.map(s => s.name),
            datasets: [{ label: `Precio Promedio — ${selectedCategories.includes('Todos') ? 'Todos' : selectedCategories.join(', ')} ($)`, data: stats.map(s => +s.avg.toFixed(2)), backgroundColor: colors, borderColor: colors.map(c => c.replace('0.8', '1')), borderWidth: 1 }],
        };
    }, [metrics, selectedCategories]);

    // Datos para histograma de distribución de precios
    const histogramData = useMemo(() => {
        if (!metrics) return null;
        const entries = Object.entries(metrics.buckets).sort((a, b) => parseFloat(a[0].slice(1)) - parseFloat(b[0].slice(1)));
        return {
            labels: entries.map(([k]) => k),
            datasets: [{ label: 'Estaciones', data: entries.map(([, v]) => v), backgroundColor: 'rgba(54, 162, 235, 0.75)', borderColor: 'rgba(54, 162, 235, 1)', borderWidth: 1 }],
        };
    }, [metrics]);

    const handleExportExcel = () => {
        if (filteredResults.length === 0) return;

        // Formatear datos para exportación
        const dataToExport = filteredResults.map((row, index) => ({
            '#': index + 1,
            'Categoría': row?.categoria || '',
            'Entidad': row?.entidadNombre || row?.entidadId,
            'Municipio': row?.municipioNombre || row?.municipioId,
            'Razón Social': row?.Nombre,
            'Producto': row?.Producto,
            'Precio': row?.PrecioVigente,
            'Dirección': row?.Direccion,
            'Descripción': row?.SubProducto,
            'Indicador': row?.Numero
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Precios Diesel");
        XLSX.writeFile(wb, "Precios_Diesel_Nacional.xlsx");
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="fas fa-sort text-muted ml-1" style={{opacity: 0.3}}></i>;
        return sortConfig.direction === 'ascending' 
            ? <i className="fas fa-sort-up ml-1"></i>
            : <i className="fas fa-sort-down ml-1"></i>;
    };

    const renderStateSortIcon = (key) => {
        if (stateSortConfig.key !== key) return <i className="fas fa-sort text-muted ml-1" style={{ opacity: 0.3 }}></i>;
        return stateSortConfig.direction === 'ascending'
            ? <i className="fas fa-sort-up ml-1"></i>
            : <i className="fas fa-sort-down ml-1"></i>;
    };

    return (
        <div className="container-fluid">
            <h1 className="h3 mb-4 text-gray-800">Scanner de Precios Diésel (Nacional)</h1>

            {/* ── KPI Cards ──────────────────────────────────────────────── */}
            {metrics && (
                <Row className="mb-4">
                    <Col xl={3} md={6} className="mb-3">
                        <Card className="border-left-primary shadow h-100 py-2">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="mr-3">
                                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">Estaciones — {selectedCategories.includes('Todos') ? 'Todos' : selectedCategories.join(', ')}</div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">{metrics.baseCount.toLocaleString()}</div>
                                        <div className="text-xs text-muted">
                                            {filteredResults.length.toLocaleString()} filtrados
                                            {filteredResults.length < metrics.total && (
                                                <span className="ml-1 text-warning">/ {metrics.total.toLocaleString()} totales</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="ml-auto"><i className="fas fa-gas-pump fa-2x text-gray-300"></i></div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3">
                        <Card className="border-left-info shadow h-100 py-2">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="mr-3">
                                        <div className="text-xs font-weight-bold text-info text-uppercase mb-1">Precio Promedio Nacional</div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">${parseFloat(metrics.avg).toFixed(2)}</div>
                                        <div className="text-xs text-muted">Dispersión: ${parseFloat(metrics.spread).toFixed(2)}</div>
                                    </div>
                                    <div className="ml-auto"><i className="fas fa-chart-bar fa-2x text-gray-300"></i></div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3">
                        <Card className="border-left-success shadow h-100 py-2">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="mr-3">
                                        <div className="text-xs font-weight-bold text-success text-uppercase mb-1">Precio Mínimo</div>
                                        <div className="h5 mb-0 font-weight-bold text-success">${parseFloat(metrics.min).toFixed(2)}</div>
                                        <div className="text-xs text-muted text-truncate" style={{ maxWidth: 160 }}>
                                            {metrics.minItem?.entidadNombre} — {metrics.minItem?.Nombre}
                                        </div>
                                    </div>
                                    <div className="ml-auto"><i className="fas fa-arrow-down fa-2x text-gray-300"></i></div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3">
                        <Card className="border-left-danger shadow h-100 py-2">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="mr-3">
                                        <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">Precio Máximo</div>
                                        <div className="h5 mb-0 font-weight-bold text-danger">${parseFloat(metrics.max).toFixed(2)}</div>
                                        <div className="text-xs text-muted text-truncate" style={{ maxWidth: 160 }}>
                                            {metrics.maxItem?.entidadNombre} — {metrics.maxItem?.Nombre}
                                        </div>
                                    </div>
                                    <div className="ml-auto"><i className="fas fa-arrow-up fa-2x text-gray-300"></i></div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            {/* ── Selector de Categoría ────────────────────────────────────── */}
            {results.length > 0 && (
                <div className="mb-4 d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                    <span className="text-xs font-weight-bold text-uppercase text-muted mr-2" style={{ whiteSpace: 'nowrap' }}>Filtrar métricas:</span>
                    {CATEGORIES.map(cat => (
                        <Button
                            key={cat}
                            size="sm"
                            variant={selectedCategories.includes(cat)
                                ? (CATEGORY_STYLE[cat]?.variant || 'secondary')
                                : 'outline-' + (CATEGORY_STYLE[cat]?.variant || 'secondary')}
                            onClick={() => toggleCategory(cat)}
                            className="mb-1"
                        >
                            <i className={`fas ${CATEGORY_STYLE[cat]?.icon || 'fa-circle'} mr-1`}></i>
                            {cat}
                            {categoryCounts && (
                                <Badge variant="light" className="ml-1" style={{ fontSize: '0.7em' }}>
                                    {(categoryCounts[cat] || 0).toLocaleString()}
                                </Badge>
                            )}
                        </Button>
                    ))}
                </div>
            )}
            {/* ── Main Card ──────────────────────────────────────────────── */}
            <Card className="shadow mb-4">
                <Card.Header className="py-3 d-flex flex-row align-items-center justify-content-between">
                    <h6 className="m-0 font-weight-bold text-primary">Resultados de Escaneo Masivo</h6>
                    <div>
                        {filteredResults.length > 0 && (
                            <Button variant="success" className="mr-2" onClick={handleExportExcel}>
                                <i className="fas fa-file-excel mr-1"></i> Exportar Excel
                            </Button>
                        )}
                        <Button variant="primary" onClick={fetchAllPrices} disabled={loading}>
                            {loading ? <><Spinner as="span" animation="border" size="sm" /> Escaneando...</> : 'Iniciar Escaneo Completo'}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {/* ── Tabs ── */}
                    <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={setActiveTab}>
                        <Nav.Item>
                            <Nav.Link eventKey="datos">
                                <i className="fas fa-table mr-1"></i> Datos
                                {results.length > 0 && <Badge variant="info" className="bg-info primary ml-2"> {results.length.toLocaleString()}</Badge> }
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="graficas" disabled={!metrics}>
                                <i className="fas fa-chart-bar mr-1"></i> Gráficas
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="estados" disabled={!metrics}>
                                <i className="fas fa-map-marker-alt mr-1"></i> Resumen por Estado
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="mapa" disabled={!metrics}>
                                <i className="fas fa-map mr-1"></i> Mapa
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {/* ════════════════ TAB: DATOS ════════════════ */}
                    {activeTab === 'datos' && (
                        <>
                            {/* ── Toggle Bitácora ── */}
                            <div
                                className="mb-3 p-3 rounded"
                                style={{
                                    background: showBitacora
                                        ? 'linear-gradient(135deg, #1a237e 0%, #283593 60%, #1565c0 100%)'
                                        : '#f8f9fc',
                                    border: showBitacora ? 'none' : '1px dashed #d1d3e2',
                                    transition: 'background 0.35s ease, box-shadow 0.35s ease',
                                    boxShadow: showBitacora ? '0 4px 15px rgba(26,35,126,0.35)' : 'none',
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
                                    {/* Left: icon + label + toggle */}
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            style={{
                                                width: 36, height: 36, borderRadius: '50%',
                                                background: showBitacora ? 'rgba(255,255,255,0.15)' : '#e8eaf6',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fas fa-fa-solid fa-filter" style={{ color: showBitacora ? '#fff' : '#3d5afe', fontSize: 15 }}></i>
                                        </div>
                                        <div>
                                            <div
                                                className="font-weight-bold text-uppercase"
                                                style={{ fontSize: '0.72rem', letterSpacing: '0.07em', color: showBitacora ? '#90caf9' : '#5a5c69' }}
                                            >
                                                Estaciones de interes
                                            </div>
                                            <div style={{ fontSize: '0.82rem', color: showBitacora ? '#fff' : '#858796', lineHeight: 1.2 }}>
                                                Sólo municipios de seguimiento
                                            </div>
                                        </div>
                                        
                                        <Form.Check
                                            type="switch"
                                            id="toggle-bitacora"
                                            label=""
                                            checked={showBitacora}
                                            onChange={e => { setShowBitacora(e.target.checked); setCurrentPage(1); }}
                                            style={{ marginLeft: 6 }}
                                        />
                                        {/* Div para dar espacio entre el texto y el toggle */}
                                        <div style={{ flexGrow: 1 }}></div>
                                    </div>

                                    {/* Right: chips */}
                                    <div className="d-flex flex-wrap" style={{ gap: 5 }}>
                                        {BITACORA_MUNICIPIOS.map(b => (
                                            <span
                                                key={`${b.entidad}-${b.municipio}`}
                                                style={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    padding: '2px 9px',
                                                    borderRadius: 20,
                                                    background: showBitacora ? 'rgba(255,255,255,0.13)' : '#e8eaf6',
                                                    color: showBitacora ? '#e3f2fd' : '#3d5afe',
                                                    border: showBitacora ? '1px solid rgba(255,255,255,0.25)' : '1px solid #c5cae9',
                                                    transition: 'all 0.3s ease',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {b.municipio}
                                                <span style={{ opacity: 0.6, marginLeft: 4, fontSize: '0.62rem' }}>
                                                    {(b.entidad==='Nuevo León') ? 'NL' :(b.entidad==='Ciudad de México') ? 'CDMX' :(b.entidad==='San Luis Potosí') ? 'SLP' : b.entidad.split(' ')[0]}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <div className="row">
                                    <div className="col-md-2 mb-2">
                                        <Form.Control type="text" name="entidad" placeholder="Entidad" value={filters.entidad} onChange={handleFilterChange} size="sm" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <Form.Control type="text" name="municipio" placeholder="Municipio" value={filters.municipio} onChange={handleFilterChange} size="sm" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <Form.Control type="text" name="razonSocial" placeholder="Razón Social" value={filters.razonSocial} onChange={handleFilterChange} size="sm" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <Form.Control type="text" name="producto" placeholder="Producto" value={filters.producto} onChange={handleFilterChange} size="sm" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <Form.Control type="text" name="descripcion" placeholder="Descripción" value={filters.descripcion} onChange={handleFilterChange} size="sm" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <Form.Control type="text" name="direccion" placeholder="Dirección" value={filters.direccion} onChange={handleFilterChange} size="sm" />
                                    </div>
                                    <div className="col-md-1 mb-2">
                                        <Button variant="outline-secondary" size="sm" onClick={clearFilters} block>
                                            <i className="fas fa-times"></i>
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive table-container">
                                <Table striped bordered hover id="dataTable" width="100%" cellSpacing="0" size="sm">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th onClick={() => requestSort('entidadId')} style={{ cursor: 'pointer' }}>Entidad {renderSortIcon('entidadId')}</th>
                                            <th onClick={() => requestSort('municipioId')} style={{ cursor: 'pointer' }}>Municipio {renderSortIcon('municipioId')}</th>
                                            <th onClick={() => requestSort('Nombre')} style={{ cursor: 'pointer' }}>Razón Social {renderSortIcon('Nombre')}</th>
                                            <th onClick={() => requestSort('Producto')} style={{ cursor: 'pointer' }}>Producto {renderSortIcon('Producto')}</th>
                                            <th onClick={() => requestSort('PrecioVigente')} style={{ cursor: 'pointer' }}>Precio {renderSortIcon('PrecioVigente')}</th>
                                            <th onClick={() => requestSort('Direccion')} style={{ cursor: 'pointer' }}>Dirección {renderSortIcon('Direccion')}</th>
                                            <th onClick={() => requestSort('SubProducto')} style={{ cursor: 'pointer' }}>Descripción {renderSortIcon('SubProducto')}</th>
                                            <th onClick={() => requestSort('Numero')} style={{ cursor: 'pointer' }}>Indicador {renderSortIcon('Numero')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && results.length === 0 && (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <Spinner animation="border" role="status"><span className="sr-only">Cargando...</span></Spinner>
                                                    <p className="mt-2">Consultando API. Esto puede tomar unos minutos...</p>
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && results.length === 0 && !error && (
                                            <tr><td colSpan="9" className="text-center">No hay datos para mostrar. Inicia un escaneo.</td></tr>
                                        )}
                                        {currentItems.map((row, index) => (
                                            <tr key={index}>
                                                <td>{indexOfFirstItem + index + 1}</td>
                                                <td>{row?.entidadNombre || row?.entidadId}</td>
                                                <td>{row?.municipioNombre || row?.municipioId}</td>
                                                <td>{row?.Nombre}</td>
                                                <td>
                                                    <span>{row?.Producto}</span>
                                                    {row?.categoria && (
                                                        <><br /><Badge variant={CATEGORY_STYLE[row.categoria]?.variant || 'secondary'} style={{ fontSize: '0.65em' }}>{row.categoria}</Badge></>
                                                    )}
                                                </td>
                                                <td className="font-weight-bold text-success">${row?.PrecioVigente}</td>
                                                <td className="small">{row?.Direccion}</td>
                                                <td className="small text-muted">{row?.SubProducto}</td>
                                                <td className="small text-muted">{row?.Numero}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredResults.length)} de {filteredResults.length} registros
                                    </div>
                                    <Pagination>
                                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {currentPage > 3 && <Pagination.Ellipsis />}
                                        {currentPage > 2 && <Pagination.Item onClick={() => handlePageChange(currentPage - 2)}>{currentPage - 2}</Pagination.Item>}
                                        {currentPage > 1 && <Pagination.Item onClick={() => handlePageChange(currentPage - 1)}>{currentPage - 1}</Pagination.Item>}
                                        <Pagination.Item active>{currentPage}</Pagination.Item>
                                        {currentPage < totalPages && <Pagination.Item onClick={() => handlePageChange(currentPage + 1)}>{currentPage + 1}</Pagination.Item>}
                                        {currentPage < totalPages - 1 && <Pagination.Item onClick={() => handlePageChange(currentPage + 2)}>{currentPage + 2}</Pagination.Item>}
                                        {currentPage < totalPages - 2 && <Pagination.Ellipsis />}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                            {!loading && results.length > 0 && totalPages <= 1 && (
                                <div className="mt-2 text-right text-muted small">
                                    Total encontrados: {filteredResults.length} / {results.length}
                                </div>
                            )}
                        </>
                    )}

                    {/* ════════════════ TAB: GRÁFICAS ════════════════ */}
                    {activeTab === 'graficas' && metrics && (
                        <Row>
                            {/* Precio promedio por estado */}
                            <Col xl={7} className="mb-4">
                                <h6 className="font-weight-bold text-primary mb-3">
                                    <i className="fas fa-chart-bar mr-1"></i> Precio Promedio — {selectedCategories.includes('Todos') ? 'Todos' : selectedCategories.join(', ')} por Estado
                                </h6>
                                <div style={{ height: 600, overflowY: 'auto' }}>
                                    <div style={{ height: Math.max(400, metrics.stateStats.length * 22) }}>
                                        <Bar
                                            data={stateChartData}
                                            options={{
                                                indexAxis: 'y',
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: ctx => ` $${ctx.parsed.x.toFixed(2)}`
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    x: {
                                                        title: { display: true, text: 'Precio ($MXN)' },
                                                        ticks: { callback: v => `$${v}` }
                                                    },
                                                    y: { ticks: { font: { size: 11 } } }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </Col>

                            {/* Distribución de precios */}
                            <Col xl={5} className="mb-4">
                                <h6 className="font-weight-bold text-primary mb-3">
                                    <i className="fas fa-chart-area mr-1"></i> Distribución de Precios — {selectedCategories.includes('Todos') ? 'Todos' : selectedCategories.join(', ')}
                                </h6>
                                <div style={{ height: 320 }}>
                                    <Bar
                                        data={histogramData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    callbacks: {
                                                        label: ctx => ` ${ctx.parsed.y.toLocaleString()} estaciones`
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: { title: { display: true, text: 'Rango de precio ($)' } },
                                                y: { title: { display: true, text: 'Número de estaciones' } }
                                            }
                                        }}
                                    />
                                </div>

                                {/* Top 5 más baratos / más caros */}
                                <Row className="mt-4">
                                    <Col xs={6}>
                                        <h6 className="text-success font-weight-bold small mb-2">
                                            <i className="fas fa-thumbs-up mr-1"></i> Top 5 más baratos
                                        </h6>
                                        <Table size="sm" bordered className="small">
                                            <tbody>
                                                {metrics.stateStats.slice(0, 5).map(s => (
                                                    <tr key={s.name}>
                                                        <td>{s.name}</td>
                                                        <td className="text-success font-weight-bold text-right">${s.avg.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Col>
                                    <Col xs={6}>
                                        <h6 className="text-danger font-weight-bold small mb-2">
                                            <i className="fas fa-thumbs-down mr-1"></i> Top 5 más caros
                                        </h6>
                                        <Table size="sm" bordered className="small">
                                            <tbody>
                                                {[...metrics.stateStats].reverse().slice(0, 5).map(s => (
                                                    <tr key={s.name}>
                                                        <td>{s.name}</td>
                                                        <td className="text-danger font-weight-bold text-right">${s.avg.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    )}

                    {/* ════════════════ TAB: RESUMEN POR ESTADO ════════════════ */}
                    {activeTab === 'estados' && metrics && (
                        <>
                            {showBitacora && (
                                <div className="mb-2 d-flex align-items-center" style={{ gap: 8 }}>
                                    <i className="fas fa-info-circle text-primary"></i>
                                    <span className="text-xs text-muted">
                                        Modo <strong>Estaciones de interés</strong> activo — haz clic en
                                        <i className="fas fa-chevron-right mx-1"></i> para ver las estaciones de cada entidad.
                                    </span>
                                </div>
                            )}
                            <div className="table-responsive">
                                <Table striped bordered hover size="sm">
                                    <thead className="thead-dark">
                                        <tr>
                                            {showBitacora && <th style={{ width: 36 }}></th>}
                                            <th onClick={() => handleStateSort('name')} style={{ cursor: 'pointer' }}>Estado {renderStateSortIcon('name')}</th>
                                            <th onClick={() => handleStateSort('count')} style={{ cursor: 'pointer' }} className="text-center">Estaciones {renderStateSortIcon('count')}</th>
                                            <th onClick={() => handleStateSort('min')} style={{ cursor: 'pointer' }} className="text-right">Mín. {renderStateSortIcon('min')}</th>
                                            <th onClick={() => handleStateSort('avg')} style={{ cursor: 'pointer' }} className="text-right">Promedio {renderStateSortIcon('avg')}</th>
                                            <th onClick={() => handleStateSort('max')} style={{ cursor: 'pointer' }} className="text-right">Máx. {renderStateSortIcon('max')}</th>
                                            <th onClick={() => handleStateSort('spread')} style={{ cursor: 'pointer' }} className="text-right">Dispersión {renderStateSortIcon('spread')}</th>
                                            <th className="text-center">Rango visual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const allAvgs = sortedStateStats.map(s => s.avg);
                                            const globalMin = Math.min(...allAvgs);
                                            const globalMax = Math.max(...allAvgs);
                                            const globalRange = globalMax - globalMin || 1;
                                            const colSpan = showBitacora ? 8 : 7;
                                            return sortedStateStats.map(s => {
                                                const norm = (s.avg - globalMin) / globalRange;
                                                const r = Math.round(norm * 200 + 30);
                                                const g = Math.round((1 - norm) * 170 + 30);
                                                const barColor = `rgb(${r}, ${g}, 60)`;
                                                const barWidth = Math.round(norm * 100);
                                                const isExpanded = expandedStates.has(s.name);

                                                // Estaciones de esta entidad dentro del filtro de interés
                                                const stateStations = showBitacora
                                                    ? filteredResults.filter(item =>
                                                        (item.entidadNombre || '').toLowerCase() === s.name.toLowerCase()
                                                    )
                                                    : [];

                                                return (
                                                    <React.Fragment key={s.name}>
                                                        <tr
                                                            style={showBitacora ? { cursor: stateStations.length > 0 ? 'pointer' : 'default' } : {}}
                                                            onClick={showBitacora && stateStations.length > 0 ? () => toggleStateExpand(s.name) : undefined}
                                                        >
                                                            {showBitacora && (
                                                                <td className="text-center" style={{ width: 36 }}>
                                                                    {stateStations.length > 0 ? (
                                                                        <i
                                                                            className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-primary`}
                                                                            style={{ fontSize: '0.75rem', transition: 'transform 0.2s' }}
                                                                        />
                                                                    ) : null}
                                                                </td>
                                                            )}
                                                            <td className="font-weight-bold">{s.name}</td>
                                                            <td className="text-center">{s.count.toLocaleString()}</td>
                                                            <td className="text-right text-success">${s.min.toFixed(2)}</td>
                                                            <td className="text-right font-weight-bold">${s.avg.toFixed(2)}</td>
                                                            <td className="text-right text-danger">${s.max.toFixed(2)}</td>
                                                            <td className="text-right text-muted">${s.spread.toFixed(2)}</td>
                                                            <td style={{ minWidth: 120 }}>
                                                                <div style={{ background: '#e9ecef', borderRadius: 3, height: 14 }}>
                                                                    <div style={{ width: `${Math.max(barWidth, 4)}%`, background: barColor, height: '100%', borderRadius: 3 }} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {showBitacora && isExpanded && stateStations.length > 0 && (() => {
                                                            const scfg = stationSortConfig[s.name] || { key: 'PrecioVigente', direction: 'ascending' };
                                                            const sortedStations = [...stateStations].sort((a, b) => {
                                                                let av = a[scfg.key] ?? '';
                                                                let bv = b[scfg.key] ?? '';
                                                                if (scfg.key === 'municipioNombre') { av = a.municipioNombre || a.municipioId || ''; bv = b.municipioNombre || b.municipioId || ''; }
                                                                if (scfg.key === 'PrecioVigente') {
                                                                    return scfg.direction === 'ascending'
                                                                        ? parseFloat(av) - parseFloat(bv)
                                                                        : parseFloat(bv) - parseFloat(av);
                                                                }
                                                                av = av.toString().toLowerCase();
                                                                bv = bv.toString().toLowerCase();
                                                                if (av < bv) return scfg.direction === 'ascending' ? -1 : 1;
                                                                if (av > bv) return scfg.direction === 'ascending' ? 1 : -1;
                                                                return 0;
                                                            });
                                                            return (
                                                            <tr>
                                                                <td colSpan={colSpan} style={{ padding: 0, background: '#f0f4ff' }}>
                                                                    <div style={{ padding: '8px 16px' }}>
                                                                        <Table size="sm" bordered className="mb-0" style={{ background: '#fff', fontSize: '0.8rem' }}>
                                                                            <thead style={{ background: '#e8eaf6' }}>
                                                                                <tr>
                                                                                    <th onClick={() => handleStationSort(s.name, 'municipioNombre')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Municipio {renderStationSortIcon(s.name, 'municipioNombre')}</th>
                                                                                    <th onClick={() => handleStationSort(s.name, 'Nombre')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Razón Social {renderStationSortIcon(s.name, 'Nombre')}</th>
                                                                                    <th onClick={() => handleStationSort(s.name, 'categoria')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Categoría {renderStationSortIcon(s.name, 'categoria')}</th>
                                                                                    <th onClick={() => handleStationSort(s.name, 'PrecioVigente')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} className="text-right">Precio {renderStationSortIcon(s.name, 'PrecioVigente')}</th>
                                                                                    <th onClick={() => handleStationSort(s.name, 'Direccion')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>Dirección {renderStationSortIcon(s.name, 'Direccion')}</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {sortedStations
                                                                                    .map((st, i) => (
                                                                                        <tr key={i}>
                                                                                            <td className="font-weight-bold text-primary">{st.municipioNombre || st.municipioId}</td>
                                                                                            <td>{st.Nombre}</td>
                                                                                            <td>
                                                                                                <Badge
                                                                                                    variant={CATEGORY_STYLE[st.categoria]?.variant || 'secondary'}
                                                                                                    style={{ fontSize: '0.65em' }}
                                                                                                >
                                                                                                    {st.categoria}
                                                                                                </Badge>
                                                                                            </td>
                                                                                            <td className="text-right font-weight-bold text-success">${parseFloat(st.PrecioVigente).toFixed(2)}</td>
                                                                                            <td className="text-muted small">{st.Direccion}</td>
                                                                                        </tr>
                                                                                    ))
                                                                                }
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            );
                                                        })()}
                                                    </React.Fragment>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="text-muted small mt-1">
                                {sortedStateStats.length} estados • {metrics.baseCount.toLocaleString()} estaciones — {selectedCategories.includes('Todos') ? 'Todos' : selectedCategories.join(', ')}
                            </div>
                        </>
                    )}

                    {/* ════════════════ TAB: MAPA ════════════════ */}
                    {activeTab === 'mapa' && (
                        <DieselMap results={results} selectedCategories={selectedCategories} />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default DieselPrices;
